// Stand-in for the Deflect Analytics endpoints until they exist. The contract they follow is
// docs/temp/deflect-analytics-backend-tz.md, and every handler applies the query parameters the
// real endpoint will — the window, the program, the comparison, paging, sorting and search — so
// the page is exercised for real against it.
//
// The lookups are generated from a fixed seed: the same set on every load, dated relative to
// today. Delete this file together with the mock branches in services.js once the endpoints land.
import dayjs from "dayjs"

import {
  COMPARE_BE_VALUES,
  getCompareRange
} from "../../../Components/Filters/CompareSelect/presets"

const ISO = "YYYY-MM-DD"
const LABEL_FORMAT = "MMM D, YYYY"
const LOOKUP_COUNT = 15000
const HISTORY_DAYS = 400

const FEES = { banking_app: 0.5, call_center: 5, mastercom: 0.5 }
const CHANNEL_LABELS = {
  banking_app: "Banking app",
  call_center: "Call center",
  mastercom: "Mastercom"
}
const PROGRAM_LABELS = { cc: "Consumer Clarity", oi: "Order Insight" }
const PROGRAMS = ["cc", "oi"]

// Mastercom lookups exist on Consumer Clarity only.
const CHANNEL_WEIGHTS = {
  cc: [
    ["banking_app", 64],
    ["call_center", 6],
    ["mastercom", 30]
  ],
  oi: [
    ["banking_app", 84],
    ["call_center", 16]
  ]
}

const MATCH_RATE = {
  cc: { banking_app: 0.89, call_center: 0.79, mastercom: 0.83 },
  oi: { banking_app: 0.81, call_center: 0.65 }
}

const DESCRIPTOR_WEIGHTS = [
  ["DROPSHIP*", 24],
  ["DROPSHIP STORE", 19],
  ["DRPSHP*SUB", 15],
  ["SHOPDROP LLC", 11],
  ["DS*PREMIUM", 8],
  ["DROPSHIP CA", 6],
  ["DRPSHP*GIFT", 5],
  ["DS*RENEWAL", 3],
  [null, 9]
]

const COUNTRY_WEIGHTS = [
  ["US", 28],
  ["GB", 16],
  ["AU", 12],
  ["DE", 10],
  ["FR", 9],
  ["CA", 8],
  ["NL", 6],
  ["ES", 4],
  ["IT", 3],
  ["SE", 2],
  ["IE", 1],
  ["PL", 1],
  ["", 3]
]

// Per program: descriptors in the Needs Help group, descriptor-program records Pending, and
// receipt-content edits in review.
const QUEUE = {
  cc: { needs_help: 1, pending: 3, profile_updates: 1 },
  oi: { needs_help: 1, pending: 2, profile_updates: 0 }
}

// mulberry32, kept to int32 so the state never loses precision.
const seeded = (seed) => {
  let state = seed
  return () => {
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), state | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const random = seeded(20260925)
const between = (min, max) => min + random() * (max - min)
const int = (min, max) => Math.floor(between(min, max + 1))

// [[value, weight], ...] -> one value, drawn in proportion to its weight.
const pick = (options) => {
  const total = options.reduce((sum, [, weight]) => sum + weight, 0)
  let roll = random() * total
  for (const [value, weight] of options) {
    roll -= weight
    if (roll < 0) return value
  }
  return options[options.length - 1][0]
}

const round2 = (value) => Math.round(value * 100) / 100

const hex = (length) => Array.from({ length }, () => "0123456789abcdef"[int(0, 15)]).join("")

const generate = () => {
  const now = dayjs()
  const cards = Array.from({ length: 6000 }, () => String(int(0, 9999)).padStart(4, "0"))

  return Array.from({ length: LOOKUP_COUNT }, (_, index) => {
    let created = now
      .subtract(Math.floor(HISTORY_DAYS * random() ** 1.3), "day")
      .hour(int(0, 23))
      .minute(int(0, 59))
      .second(0)
    if (created.isAfter(now)) created = now.subtract(int(5, 300), "minute")

    const program = random() < 0.6 ? "cc" : "oi"
    const channel = pick(CHANNEL_WEIGHTS[program])
    // A small pool of cards asks again and again, so repeat cardholders exist in any window.
    const cardIndex = random() < 0.12 ? int(0, 79) : int(0, cards.length - 1)

    return {
      lookup_id: `${hex(8)}-${hex(4)}-4${hex(3)}-${hex(4)}-${hex(12)}`,
      index,
      card_key: cardIndex,
      card_last4: cards[cardIndex],
      program,
      channel,
      matched: random() < MATCH_RATE[program][channel],
      amount: round2(Math.exp(between(Math.log(20), Math.log(600)))),
      descriptor: pick(DESCRIPTOR_WEIGHTS),
      country: pick(COUNTRY_WEIGHTS),
      created
    }
  }).sort((a, b) => b.created.valueOf() - a.created.valueOf())
}

let dataset = null
const allLookups = () => {
  if (!dataset) dataset = generate()
  return dataset
}

/* ------------- Query helpers ------------- */

const csv = (value) =>
  value
    ? String(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : []

const frameOf = (start, end) =>
  start && end ? [dayjs(start).startOf("day"), dayjs(end).endOf("day")] : null

const inFrame = (at, frame) => !frame || (!at.isBefore(frame[0]) && !at.isAfter(frame[1]))

const programsOf = (params) => {
  const wanted = csv(params.program)
  return wanted.length ? PROGRAMS.filter((p) => wanted.includes(p)) : PROGRAMS
}

const scoped = (params, frame = frameOf(params.start_date, params.end_date)) => {
  const programs = programsOf(params)
  return allLookups().filter((l) => programs.includes(l.program) && inFrame(l.created, frame))
}

const COMPARE_ID_OF = Object.fromEntries(
  Object.entries(COMPARE_BE_VALUES).map(([id, value]) => [value, id])
)

const comparisonPeriodOf = (params) => {
  if (!params.compare) return null
  const [from, to] =
    params.compare === "custom"
      ? [params.compare_start_date, params.compare_end_date]
      : getCompareRange([params.start_date, params.end_date], COMPARE_ID_OF[params.compare])
  if (!from || !to) return null
  return {
    start_date: from,
    end_date: to,
    type: params.compare,
    label: `${dayjs(from).format(LABEL_FORMAT)} – ${dayjs(to).format(LABEL_FORMAT)}`
  }
}

const comparedOf = (params, period) =>
  period ? scoped(params, frameOf(period.start_date, period.end_date)) : null

const groupsOf = (list, keyOf) => {
  const groups = new Map()
  for (const item of list) {
    const key = keyOf(item)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(item)
  }
  return groups
}

const sumOf = (list, valueOf) => round2(list.reduce((sum, item) => sum + valueOf(item), 0))
const feeOf = (lookup) => FEES[lookup.channel]
const shareOf = (part, total) => (total ? round2((part / total) * 100) : 0)

const tally = (list) => {
  const matched = list.filter((l) => l.matched).length
  return {
    lookups_count: list.length,
    matched_count: matched,
    match_rate: list.length ? round2((matched / list.length) * 100) : null,
    fees_amount: sumOf(list, feeOf)
  }
}

const paginate = (list, params, defaultSize) => {
  const page = Math.max(1, Number(params.page) || 1)
  const size = Number(params.page_size) || defaultSize
  return {
    count: list.length,
    next: page * size < list.length ? `?page=${page + 1}` : null,
    previous: page > 1 ? `?page=${page - 1}` : null,
    results: list.slice((page - 1) * size, page * size)
  }
}

/* ------------- Trend buckets ------------- */

const unitOf = (days) => (days <= 31 ? "day" : days <= 180 ? "week" : "month")

const stepOf = (unit) => (unit === "week" ? [7, "day"] : [1, unit])

const boundsOf = (params) => {
  if (params.start_date && params.end_date)
    return [dayjs(params.start_date), dayjs(params.end_date)]
  const lookups = allLookups()
  return [lookups[lookups.length - 1].created, dayjs()]
}

const timelineOf = ([from, to]) => {
  const unit = unitOf(to.diff(from, "day") + 1)
  const first = unit === "month" ? from.startOf("month") : from.startOf("day")
  const [amount, stepUnit] = stepOf(unit)
  const labels = []
  for (let cur = first; !cur.isAfter(to); cur = cur.add(amount, stepUnit)) {
    labels.push(cur.format(ISO))
  }
  return { unit, first, labels }
}

// The comparison window on the same unit and the same number of buckets, so the overlay lines up
// with the current window by index.
const comparisonTimelineOf = (timeline, period) => {
  const start = dayjs(period.start_date)
  const first = timeline.unit === "month" ? start.startOf("month") : start.startOf("day")
  const [amount, stepUnit] = stepOf(timeline.unit)
  return {
    ...timeline,
    first,
    labels: timeline.labels.map((_, index) => first.add(index * amount, stepUnit).format(ISO))
  }
}

const bucketIndex = (at, { unit, first }) => {
  if (unit === "month") return at.startOf("month").diff(first, "month")
  const days = at.startOf("day").diff(first, "day")
  return unit === "week" ? Math.floor(days / 7) : days
}

const bucketed = (list, timeline) => {
  const buckets = timeline.labels.map(() => [])
  for (const l of list) {
    const index = bucketIndex(l.created, timeline)
    if (index >= 0 && index < buckets.length) buckets[index].push(l)
  }
  return buckets
}

const metricOf = (list, metric) => {
  switch (metric) {
    case "deflected":
      return sumOf(
        list.filter((l) => l.matched),
        (l) => l.amount
      )
    case "match_rate":
      return tally(list).match_rate
    case "fees":
      return sumOf(list, feeOf)
    default:
      return list.length
  }
}

/* ------------- Endpoints ------------- */

const kpiSummaryOf = (list) => {
  const matched = list.filter((l) => l.matched)
  const deflected = sumOf(matched, (l) => l.amount)
  return {
    lookups_count: list.length,
    matched_count: matched.length,
    unmatched_count: list.length - matched.length,
    match_rate: tally(list).match_rate,
    deflected_amount: deflected,
    avg_order_amount: matched.length ? round2(deflected / matched.length) : 0,
    fees_amount: sumOf(list, feeOf)
  }
}

export const getKpis = (params) => {
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  return {
    comparison_period: period,
    summary: kpiSummaryOf(scoped(params)),
    comparison_summary: compared ? kpiSummaryOf(compared) : null
  }
}

export const getTrend = (params) => {
  const metric = params.metric || "lookups"
  const timeline = timelineOf(boundsOf(params))
  const period = comparisonPeriodOf(params)
  const comparisonTimeline = period ? comparisonTimelineOf(timeline, period) : null
  return {
    comparison_period: period,
    metric,
    unit: timeline.unit,
    labels: timeline.labels,
    data: bucketed(scoped(params), timeline).map((bucket) => metricOf(bucket, metric)),
    comparison: comparisonTimeline
      ? {
          labels: comparisonTimeline.labels,
          data: bucketed(comparedOf(params, period), comparisonTimeline).map((bucket) =>
            metricOf(bucket, metric)
          )
        }
      : null
  }
}

export const getQueue = (params) =>
  programsOf(params).reduce(
    (sum, program) => ({
      needs_help: sum.needs_help + QUEUE[program].needs_help,
      pending: sum.pending + QUEUE[program].pending,
      profile_updates: sum.profile_updates + QUEUE[program].profile_updates
    }),
    { needs_help: 0, pending: 0, profile_updates: 0 }
  )

export const getFlow = (params) => ({
  result: [...groupsOf(scoped(params), (l) => `${l.program}:${l.channel}`).values()].map(
    (items) => ({
      program: items[0].program,
      channel: items[0].channel,
      lookups_count: items.length,
      matched_count: items.filter((l) => l.matched).length,
      fees_amount: sumOf(items, feeOf),
      matched_fees_amount: sumOf(
        items.filter((l) => l.matched),
        feeOf
      )
    })
  )
})

const channelSummaryOf = (list) => {
  const { lookups_count, matched_count, match_rate, fees_amount } = tally(list)
  return {
    lookups_count,
    matched_count,
    match_rate,
    fees_amount,
    avg_fee: lookups_count ? round2(fees_amount / lookups_count) : 0,
    order_amount: sumOf(list, (l) => l.amount)
  }
}

export const getByChannel = (params) => {
  const list = scoped(params)
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  const result = [...groupsOf(list, (l) => l.channel)]
    .map(([channel, items]) => ({
      channel,
      label: CHANNEL_LABELS[channel],
      unit_fee: FEES[channel],
      ...tally(items),
      order_amount: sumOf(items, (l) => l.amount),
      share: shareOf(items.length, list.length)
    }))
    .sort((a, b) => b.lookups_count - a.lookups_count)

  return {
    comparison_period: period,
    result,
    summary: channelSummaryOf(list),
    comparison_summary: compared ? channelSummaryOf(compared) : null
  }
}

const countrySummaryOf = (list) => {
  const located = list.filter((l) => l.country)
  const countries = [...groupsOf(located, (l) => l.country)]
  const top = countries.sort((a, b) => b[1].length - a[1].length)[0]
  return {
    lookups_count: located.length,
    fees_amount: sumOf(located, feeOf),
    countries_count: countries.length,
    top_country_code: top ? top[0] : null
  }
}

export const getByCountry = (params) => {
  const located = scoped(params).filter((l) => l.country)
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  const result = [...groupsOf(located, (l) => l.country)]
    .map(([code, items]) => {
      const fees = sumOf(items, feeOf)
      return {
        country_code: code,
        lookups_count: items.length,
        fees_amount: fees,
        avg_fee: round2(fees / items.length),
        share: shareOf(items.length, located.length)
      }
    })
    .sort((a, b) => b.lookups_count - a.lookups_count)

  return {
    comparison_period: period,
    result,
    summary: countrySummaryOf(located),
    comparison_summary: compared ? countrySummaryOf(compared) : null
  }
}

export const getByProgram = (params) => {
  const list = scoped(params)
  return {
    result: [...groupsOf(list, (l) => l.program)]
      .map(([program, items]) => ({
        program,
        label: PROGRAM_LABELS[program],
        ...tally(items),
        share: shareOf(items.length, list.length)
      }))
      .sort((a, b) => b.lookups_count - a.lookups_count)
  }
}

export const getMatchRateMatrix = (params) => {
  const list = scoped(params)
  const programs = programsOf(params)
  return {
    programs,
    rows: Object.keys(CHANNEL_LABELS).map((channel) => ({
      channel,
      label: CHANNEL_LABELS[channel],
      cells: programs.map((program) => {
        const { lookups_count, matched_count, match_rate } = tally(
          list.filter((l) => l.channel === channel && l.program === program)
        )
        return { program, lookups_count, matched_count, match_rate }
      })
    }))
  }
}

const mostFrequent = (items, keyOf) =>
  [...groupsOf(items, keyOf)].sort((a, b) => b[1].length - a[1].length)[0][0]

const repeatRowsOf = (list) =>
  [...groupsOf(list, (l) => l.card_key)]
    .filter(([, items]) => items.length > 1)
    .map(([, items]) => {
      const matched = items.filter((l) => l.matched).length
      const channel = mostFrequent(items, (l) => l.channel)
      return {
        card_last4: items[0].card_last4,
        program: mostFrequent(items, (l) => l.program),
        top_channel: channel,
        top_channel_label: CHANNEL_LABELS[channel],
        lookups_count: items.length,
        matched_count: matched,
        match_rate: round2((matched / items.length) * 100),
        order_amount: sumOf(items, (l) => l.amount),
        // The list is sorted newest first, so a card's first lookup here is its latest.
        last_lookup_at: items[0].created.toISOString()
      }
    })
    .sort((a, b) => b.lookups_count - a.lookups_count || b.order_amount - a.order_amount)
    .map((row, index) => ({ sequence_number: index + 1, ...row }))

const repeatSummaryOf = (rows) => {
  const lookups = rows.reduce((sum, r) => sum + r.lookups_count, 0)
  const matched = rows.reduce((sum, r) => sum + r.matched_count, 0)
  return {
    cards_count: rows.length,
    lookups_count: lookups,
    matched_count: matched,
    match_rate: lookups ? round2((matched / lookups) * 100) : null,
    order_amount: round2(rows.reduce((sum, r) => sum + r.order_amount, 0))
  }
}

const REPEAT_ORDERING = [
  "lookups_count",
  "matched_count",
  "order_amount",
  "match_rate",
  "last_lookup_at"
]

const orderRows = (rows, ordering) => {
  const field = ordering.replace(/^-/, "")
  if (!REPEAT_ORDERING.includes(field)) return rows
  const direction = ordering.startsWith("-") ? -1 : 1
  return [...rows].sort((a, b) => {
    const diff = a[field] < b[field] ? -1 : a[field] > b[field] ? 1 : 0
    return direction * diff || a.sequence_number - b.sequence_number
  })
}

export const getRepeatCardholders = (params) => {
  const rows = repeatRowsOf(scoped(params))
  const period = comparisonPeriodOf(params)
  const compared = comparedOf(params, period)
  const term = (params.search || "").trim()
  const found = term ? rows.filter((r) => r.card_last4.includes(term)) : rows

  return {
    comparison_period: period,
    ...paginate(orderRows(found, params.ordering || "-lookups_count"), params, 20),
    summary: repeatSummaryOf(rows),
    comparison_summary: compared ? repeatSummaryOf(repeatRowsOf(compared)) : null
  }
}

export const getChannelTrends = (params) => {
  const list = scoped(params)
  const timeline = timelineOf(boundsOf(params))
  const buckets = bucketed(list, timeline)
  const channels = [...groupsOf(list, (l) => l.channel)]
    .sort((a, b) => b[1].length - a[1].length)
    .map(([channel]) => channel)

  return {
    unit: timeline.unit,
    labels: timeline.labels,
    series: channels.map((channel) => {
      const inSeries = buckets.map((bucket) => bucket.filter((l) => l.channel === channel))
      return {
        key: channel,
        label: CHANNEL_LABELS[channel],
        count: inSeries.map((items) => items.length),
        fees: inSeries.map((items) => sumOf(items, feeOf))
      }
    })
  }
}
